import React from 'react';
import { Plus, Trash2, Loader2, Zap, Settings, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { TRIGGER_TYPES, ACTION_TYPES } from './workflowConstants';

export const WorkflowEditor = ({ 
  open, onOpenChange, formData, setFormData, 
  templates, editingWorkflow, onSave, isSaving 
}) => {
  const addAction = () => {
    setFormData(prev => ({
      ...prev,
      actions: [...prev.actions, { type: 'send_whatsapp', template_id: '', delay_minutes: 0 }]
    }));
  };

  const updateAction = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  };

  const removeAction = (index) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="workflow-editor-modal">
        <DialogHeader>
          <DialogTitle>{editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}</DialogTitle>
          <DialogDescription>Define triggers and automated actions for customer engagement</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <Label>Workflow Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Quote Follow-Up Reminder" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Describe what this workflow does..." />
            </div>
          </div>

          {/* Trigger */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#fb6402]" />Trigger
            </h4>
            <div>
              <Label>When should this workflow run? *</Label>
              <Select value={formData.trigger_type} onValueChange={(v) => setFormData(prev => ({ ...prev, trigger_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Select a trigger..." /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map(trigger => (
                    <SelectItem key={trigger.value} value={trigger.value}>
                      <span className="flex items-center gap-2">
                        <trigger.icon className={`h-4 w-4 ${trigger.color}`} />{trigger.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Delay before executing (minutes)</Label>
              <Input type="number" value={formData.trigger_delay_minutes} onChange={(e) => setFormData(prev => ({ ...prev, trigger_delay_minutes: parseInt(e.target.value) || 0 }))} min="0" />
              <p className="text-xs text-gray-500 mt-1">Set to 0 for immediate execution, or add a delay (e.g., 1440 = 24 hours)</p>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2">
                <Settings className="h-4 w-4 text-purple-500" />Actions
              </h4>
              <Button variant="outline" size="sm" onClick={addAction}><Plus className="h-4 w-4 mr-1" />Add Action</Button>
            </div>

            {formData.actions.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed rounded-lg">
                <p className="text-gray-500">No actions added yet</p>
                <Button variant="link" onClick={addAction}>Add your first action</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.actions.map((action, index) => (
                  <Card key={`action-${action.type}-${index}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 space-y-3">
                          <Select value={action.type} onValueChange={(v) => updateAction(index, 'type', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ACTION_TYPES.map(a => (
                                <SelectItem key={a.value} value={a.value}>
                                  <span className="flex items-center gap-2"><a.icon className="h-4 w-4" />{a.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {(action.type === 'send_whatsapp' || action.type === 'send_email') && (
                            <Select value={action.template_id || ''} onValueChange={(v) => updateAction(index, 'template_id', v)}>
                              <SelectTrigger><SelectValue placeholder="Select template..." /></SelectTrigger>
                              <SelectContent>
                                {templates
                                  .filter(t => action.type === 'send_whatsapp' ? t.template_type === 'whatsapp' : t.template_type === 'email')
                                  .map(t => (
                                    <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          )}

                          {action.type === 'assign_tag' && (
                            <Input placeholder="Tag name..." value={action.tag || ''} onChange={(e) => updateAction(index, 'tag', e.target.value)} />
                          )}

                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Delay:</Label>
                            <Input type="number" className="w-24" value={action.delay_minutes || 0} onChange={(e) => updateAction(index, 'delay_minutes', parseInt(e.target.value) || 0)} min="0" />
                            <span className="text-xs text-gray-500">minutes after trigger</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeAction(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Targeting */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-cyan-500" />Targeting (Optional)
            </h4>
            <p className="text-sm text-gray-500">Leave empty to target all customers</p>
            
            <div>
              <Label>Customer Types</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['individual', 'student', 'business', 'corporate'].map(type => (
                  <Badge key={type} variant={formData.target_customer_types.includes(type) ? 'default' : 'outline'} className="cursor-pointer"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      target_customer_types: prev.target_customer_types.includes(type)
                        ? prev.target_customer_types.filter(t => t !== type) : [...prev.target_customer_types, type]
                    }))}
                  >{type}</Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Lifecycle Stages</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['lead', 'prospect', 'first_time', 'repeat', 'loyal', 'at_risk', 'churned'].map(stage => (
                  <Badge key={stage} variant={formData.target_lifecycle_stages.includes(stage) ? 'default' : 'outline'} className="cursor-pointer"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      target_lifecycle_stages: prev.target_lifecycle_stages.includes(stage)
                        ? prev.target_lifecycle_stages.filter(s => s !== stage) : [...prev.target_lifecycle_stages, stage]
                    }))}
                  >{stage.replace('_', ' ')}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={isSaving} data-testid="save-workflow-btn">
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {editingWorkflow ? 'Update Workflow' : 'Create Workflow'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
